import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    IClientFunnelData,
    IClientTimeToHireData,
    IClientSourceOfHireStats,
    IClientSourceOfHireChartData,
    IFetchSourceOfHireParams,
} from '../../../common';
import { IOption } from '../../../base/base-forms/interface';
import {
    IFetchFunnelAction,
    ISetFunnelAction,
    ISetReportFilterOptionsAction,
    IFetchTimeToHireAction,
    ISetTimeToHireAction,
    IFetchSourceOfHireAction,
    ISetSourceOfHireStatsAction,
    ISetSourceOfHireChartAction,
    ISetSourceOfHireFilterAction,
} from './interface';

interface IReportsSlice {
    funnelData: IClientFunnelData | null;
    timeToHireData: IClientTimeToHireData | null;
    sourceOfHireStats: IClientSourceOfHireStats | null;
    sourceOfHireChart: IClientSourceOfHireChartData | null;
    sourceOfHireFilter: IFetchSourceOfHireParams;
    jobsOptions: IOption[];
    recruitersOptions: IOption[];
    uiState: {
        isLoadingFunnel: boolean;
        isLoadingTimeToHire: boolean;
        isLoadingSourceOfHireStats: boolean;
        isLoadingSourceOfHireChart: boolean;
        isLoadingFilterOptions: boolean;
    };
}

const initialState: IReportsSlice = {
    funnelData: null,
    timeToHireData: null,
    sourceOfHireStats: null,
    sourceOfHireChart: null,
    sourceOfHireFilter: {},
    jobsOptions: [],
    recruitersOptions: [],
    uiState: {
        isLoadingFunnel: false,
        isLoadingTimeToHire: false,
        isLoadingSourceOfHireStats: false,
        isLoadingSourceOfHireChart: false,
        isLoadingFilterOptions: false,
    },
};

export const sliceName = 'reports';
export const reportsSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        fetchFunnel: (
            state,
            _action: PayloadAction<IFetchFunnelAction>
        ) => {
            state.uiState.isLoadingFunnel = true;
        },
        setFunnel: (
            state,
            action: PayloadAction<ISetFunnelAction>
        ) => {
            state.funnelData = action.payload.funnelData;
            state.uiState.isLoadingFunnel = false;
        },
        fetchFunnelDone: (state) => {
            state.uiState.isLoadingFunnel = false;
        },
        fetchTimeToHire: (
            state,
            _action: PayloadAction<IFetchTimeToHireAction>
        ) => {
            state.uiState.isLoadingTimeToHire = true;
        },
        setTimeToHire: (
            state,
            action: PayloadAction<ISetTimeToHireAction>
        ) => {
            state.timeToHireData = action.payload.timeToHireData;
            state.uiState.isLoadingTimeToHire = false;
        },
        fetchTimeToHireDone: (state) => {
            state.uiState.isLoadingTimeToHire = false;
        },
        fetchSourceOfHireStats: (
            state,
            _action: PayloadAction<IFetchSourceOfHireAction>
        ) => {
            state.uiState.isLoadingSourceOfHireStats = true;
        },
        setSourceOfHireStats: (
            state,
            action: PayloadAction<ISetSourceOfHireStatsAction>
        ) => {
            state.sourceOfHireStats = action.payload.stats;
            state.uiState.isLoadingSourceOfHireStats = false;
        },
        fetchSourceOfHireStatsDone: (state) => {
            state.uiState.isLoadingSourceOfHireStats = false;
        },
        fetchSourceOfHireChart: (
            state,
            _action: PayloadAction<IFetchSourceOfHireAction>
        ) => {
            state.uiState.isLoadingSourceOfHireChart = true;
        },
        setSourceOfHireChart: (
            state,
            action: PayloadAction<ISetSourceOfHireChartAction>
        ) => {
            state.sourceOfHireChart = action.payload.chart;
            state.uiState.isLoadingSourceOfHireChart = false;
        },
        fetchSourceOfHireChartDone: (state) => {
            state.uiState.isLoadingSourceOfHireChart = false;
        },
        setSourceOfHireFilter: (
            state,
            action: PayloadAction<ISetSourceOfHireFilterAction>
        ) => {
            state.sourceOfHireFilter = action.payload.filter;
        },
        fetchReportFilterOptions: (state) => {
            state.uiState.isLoadingFilterOptions = true;
        },
        setReportFilterOptions: (
            state,
            action: PayloadAction<ISetReportFilterOptionsAction>
        ) => {
            if (action.payload.jobsOptions) {
                state.jobsOptions = action.payload.jobsOptions;
            }
            if (action.payload.recruitersOptions) {
                state.recruitersOptions = action.payload.recruitersOptions;
            }
            state.uiState.isLoadingFilterOptions = false;
        },
        fetchReportFilterOptionsDone: (state) => {
            state.uiState.isLoadingFilterOptions = false;
        },
    },
});

export const reportsActions = reportsSlice.actions;
export default reportsSlice.reducer;
